import { useState, useRef, useEffect } from "react";

const CategoryMultiSelect = ({
  selectedOptions,
  onSelectedOptionsChange,
  multiSelectOptions,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleOption = (option) => {
    const isSelected = selectedOptions.some((item) => item.value === option.value);
    if (isSelected) {
      const newOptions = selectedOptions.filter((item) => item.value !== option.value);
      onSelectedOptionsChange(newOptions);
    } else if (selectedOptions.length < 3) {
      const newOptions = [...selectedOptions, option];
      onSelectedOptionsChange(newOptions);
    }
  };

  const removeOption = (option, e) => {
    e.stopPropagation();
    const newOptions = selectedOptions.filter((item) => item.value !== option.value);
    onSelectedOptionsChange(newOptions);
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className={`w-full p-3 pr-10 border rounded-lg focus-within:ring-1 focus-within:ring-green-500 focus-within:border-green-500 transition-colors bg-white cursor-pointer border-gray-300 min-h-[48px] max-h-[48px] overflow-hidden ${
          selectedOptions.length === 0 ? "text-gray-400" : "text-gray-900"
        }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedOptions.length === 0 ? (
          <span className="text-gray-400">Select category...</span>
        ) : (
          <div className="flex gap-1 overflow-x-auto" style={{ scrollbarWidth: 'thin' }}>
            {selectedOptions.map((option) => (
              <span
                key={option.value}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded whitespace-nowrap flex-shrink-0"
              >
                {option.label}
                <button
                  type="button"
                  className="hover:text-green-900"
                  onClick={(e) => removeOption(option, e)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none z-10">
        <svg
          className="w-5 h-5 text-gray-400 transition-transform duration-200"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          <div className="p-2">
            {selectedOptions.length >= 3 && (
              <div className="px-3 py-2 text-xs text-orange-600 bg-orange-50 rounded mb-2">
                Maximum 3 categories
              </div>
            )}
            {multiSelectOptions?.length > 0 ? (
              multiSelectOptions.map((option) => {
                const isSelected = selectedOptions.some((item) => item.value === option.value);
                const isDisabled = !isSelected && selectedOptions.length >= 3;
                return (
                  <label
                    key={option.value}
                    className={`flex items-center gap-2 px-3 py-2 text-sm rounded ${
                      isSelected
                        ? "bg-green-100 text-green-900 font-medium cursor-pointer"
                        : isDisabled
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 cursor-pointer"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={() => !isDisabled && toggleOption(option)}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 cursor-pointer disabled:cursor-not-allowed"
                    />
                    <span>{option.label}</span>
                  </label>
                );
              })
            ) : (
              <div className="p-2 text-sm text-center text-gray-500">No options found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryMultiSelect;
